import React from 'react';
import { TableOfContents } from 'components/TableOfContents';
import { TopicSubNav } from 'components/TopicSubNav';
import { UserInteractionTracker } from 'components/UserInteractionTracker';

import { propertyEquals } from 'lib/functionUtils';

/**
 * @typedef {import('lib/graphqlTypeDefs.mjs').CuratedList} SubNav
 * @property {boolean} isEnabled - Whether the sub-navigation is enabled.
 */

/**
 * @typedef {object} TableOfContents
 * @property {boolean} isEnabled - Whether the table of contents is enabled.
 * @property {string} title - The title of the table of contents.
 * @property {Array} links - The list of links in the table of contents.
 * @property {boolean} visible - Whether the table of contents is visible.
 * @property {string} activeTableOfContentsSection - Identifies the active section
 */

/**
 * ContentNav contains "nav"-like items that show up regardless of content rendered in chromeless mode
 *
 * @param {object} props - The component props.
 * @param {string} props.articleId - The ID of the current article.
 * @param {SubNav} props.subnav - Configuration for the topic sub-navigation.
 * @param {TableOfContents} props.tableOfContents - Configuration for the table of contents.
 * @returns {JSX.Element} The rendered ContentNav component.
 */
export const ContentNav = ({
  subnav,
  articleId,
  tableOfContents,
}) => (
  <>
    {subnav.isEnabled && (
      <TopicSubNav
        titleText={subnav.headline?.primary}
        titleLink={subnav.externalUrl}
        linkItems={subnav.content?.items}
        isActiveLink={propertyEquals('id', articleId)}
        logo={subnav.teaseImage}
      />
    )}
    {tableOfContents.isEnabled && (
      <UserInteractionTracker events={['click', 'keydown']}>
        {(lastInteraction) => (
          <TableOfContents
            activeLink={tableOfContents.activeTableOfContentsSection}
            lastInteraction={lastInteraction}
            links={tableOfContents.links}
            testId="article-table-of-content"
            title={tableOfContents.title}
            variant="topnav"
            visible={tableOfContents.visible}
          />
        )}
      </UserInteractionTracker>
    )}
  </>
);
